import { IkeUnit } from '@ikegps/core-shared';
import { action, makeObservable } from 'mobx';
import { ImageButton } from '../../../components/button/image.button';
import { MaterialIcon } from '../../../components/icon/icons';
import { CSS as MenuItemCSS } from '../../../components/menu/menu.item.style';
import { addStyles, DefaultTheme } from '../../../components/theme';
import { T, Translate } from '../../../i18n';
import { m } from '../../../mithril';
import { Services } from '../../../services';
import { FormDesigner } from '../form.designer';
import { FormFieldEditorHeight } from './form.field.editor.height';
import { IkeToken } from '@ikegps/core-api';
import { classNames } from '../../../util/classnames';

const CSS = {
	container: 'c-FdcVertSep',
	title: 'c-FdcVertSep__title',
	list: 'c-FdcVertSep__list',
	menu: 'c-FdcVertSep__menu',
	menuContainer: 'c-FdcVertSep__menuContainer',
	option: 'c-FdcVertSep__option',
	value: 'c-FdcVertSep__value',
	ghost: 'c-FdcVertSep__ghost',
	disabled: 'c-FdcVertSep__disabled',
	icon: {
		icon: 'c-FdcVertSep__icon',
		label: 'c-FdcVertSep__icon-label',
	},
	unit: 'c-FdcVertSep__unit',
	selected: 'c-FdcVertSep__selected',
	input: 'c-Input__input',
	MDL: {
		input: 'mdl-textfield__input',
	},
};

addStyles({
	[CSS.container]: {
		width: '100%',
		display: 'flex',
		'flex-direction': 'column',
	},
	[CSS.list]: {
		display: 'flex',
		width: '100%',
		'align-items': 'center',
		'& > div': {
			flex: 1,
		},
	},
	[CSS.title]: {
		'font-weight': 'bold',
		'margin-bottom': DefaultTheme.space(1),
		color: DefaultTheme.font.color.supporting,
	},
	[CSS.option]: {
		width: '320px',
		'text-align': 'left',
		display: 'flex',
		'align-items': 'center',
	},
	[CSS.selected]: {
		width: '320px',
		'text-align': 'left',
		display: 'flex',
		'align-items': 'center',
	},
	[CSS.ghost]: {
		color: DefaultTheme.font.color.supporting,
	},
	[CSS.disabled]: {
		opacity: '0.5',
	},
	[CSS.value]: {
		width: '150px',
		'text-align': 'left',
	},
	[CSS.unit]: {
		'font-weight': 700,
		'margin-left': '4px',
	},
	[CSS.icon.icon]: {
		margin: '0 4px',
		'font-style': 'normal',
	},
	[CSS.icon.label]: {
		'border-radius': '50%',
		border: '1px solid ' + DefaultTheme.background.grey,
		'background-color': DefaultTheme.background.lightGrey,
		'font-weight': 'bold',
		'font-size': '12px',
		height: '16px',
		width: '16px',
		display: 'flex',
		'align-items': 'center',
		'justify-content': 'center',
		margin: '0 8px',
	},
	[CSS.menu]: {
		[`& .${MenuItemCSS.item}`]: {
			'padding-top': DefaultTheme.space(1),
			'padding-bottom': DefaultTheme.space(1),
			'line-height': DefaultTheme.space(6),
		},
	},
	[CSS.menuContainer]: {
		display: 'flex',
		position: 'relative',
		'align-items': 'center',
	},
});

interface ClearanceFieldInputOptions {
	clearance: number;
}

export class FormFieldEditorVerticalSeparation extends FormFieldEditorHeight {
	clearanceField: ClearanceFieldInputOptions;

	constructor(designer: FormDesigner, field) {
		super(designer, field);
		makeObservable(this);
	}

	private getClearanceNode(disabled: boolean) {
		this.clearanceField = {
			clearance:
				this.field.clearanceThresholdInMeters !== null
					? Math.round(
							IkeUnit.convert(
								this.field.clearanceThresholdInMeters,
								Services.UserSettings.units === 'meters' ? IkeUnit.CENTIMETER : IkeUnit.INCH,
								IkeUnit.METER
							)
						)
					: null,
		};

		return m('div', { className: CSS.container }, [
			m('div', { className: `${CSS.option} ${CSS.title}` }, Translate(T.FIELD_VERTICAL_SEPARATION_CLEARANCE)),
			m('div', { className: CSS.menuContainer }, [
				m('input', {
					className: `${CSS.input} ${CSS.MDL.input} ${CSS.value}`,
					type: 'number',
					placeholder: `Clearance in ${Services.UserSettings.units === 'meters' ? 'cm' : 'inches'}`,
					value: this.clearanceField.clearance,
					disabled,
					oninput: (e) => this.changeClearance({ clearance: parseFloat((e.target as HTMLInputElement).value) }),
				}),
				m('sup', { className: `${CSS.unit}` }, Services.UserSettings.units === 'meters' ? 'cm' : 'inches'),
				ImageButton.view(MaterialIcon.DELETE, {
					onclick: () => {
						this.setClearanceThresholdInMeters(null);
					},
					disabled,
				}),
			]),
		]);
	}

	protected override getLabelWithNode(disabled?: boolean) {
		const node = super.getLabelWithNode();
		if (!node || !disabled) return node;

		// Wrap in a container that prevents interaction when disabled
		return m('div', { style: { pointerEvents: 'none' } }, node);
	}

	// Default view
	override viewInfoRW() {
		const isDisabled = !Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE);

		return m(
			'div',
			{
				className: classNames(CSS.list, { [CSS.disabled]: isDisabled }),
				title: isDisabled ? Translate(T.FORM_OPTION_PRO_ONLY_TITLE) : undefined,
			},
			[this.getLabelWithNode(isDisabled), this.getClearanceNode(isDisabled)]
		);
	}

	@action.bound private changeClearance(key: ClearanceFieldInputOptions): any {
		var clearanceInMeters = 0;
		if (key.clearance !== null) {
			clearanceInMeters = IkeUnit.convert(
				key.clearance,
				IkeUnit.METER,
				Services.UserSettings.units === 'meters' ? IkeUnit.CENTIMETER : IkeUnit.INCH
			);
		}
		this.setClearanceThresholdInMeters(clearanceInMeters);
		this.clearanceField = key;
	}
}
